import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/context/LanguageContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
  Lock, ArrowLeft, Share2, CheckCircle2, FileText, Clock, Download,
  Upload, DollarSign, Copy, Calendar, User, Shield, AlertTriangle,
  Video, Award, AlertCircle, MessageSquare
} from 'lucide-react';
import { toast } from 'sonner';
import { formatCurrency } from '@/utils/currency';
import VideoSequenceFlow from '@/components/VideoSequenceFlow';
import VideoHistory from '@/components/VideoHistory';
import ClarificationDialog from '@/components/ClarificationDialog';
import InviteShareDialog from '@/components/InviteShareDialog';

function copyToClipboard(text) {
  if (navigator.clipboard && navigator.clipboard.writeText) {
    return navigator.clipboard.writeText(text).catch(() => fallbackCopy(text));
  }
  return fallbackCopy(text);
}

function fallbackCopy(text) {
  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.style.position = 'fixed';
  textarea.style.opacity = '0';
  document.body.appendChild(textarea);
  textarea.select();
  try { document.execCommand('copy'); } catch (e) { /* ignore */ }
  document.body.removeChild(textarea);
  return Promise.resolve();
}

const statusConfig = {
  DRAFT: { className: 'bg-muted text-muted-foreground border-border', label: 'Draft', icon: FileText },
  PENDING: { className: 'bg-warning/10 text-warning border-warning/30', label: 'Pending', icon: Clock },
  BORROWER_CONFIRMED: { className: 'bg-blue-50 text-blue-700 border-blue-200', label: 'Borrower Confirmed', icon: CheckCircle2 },
  CLARIFICATION_REQUESTED: { className: 'bg-orange-50 text-orange-700 border-orange-200', label: 'Clarification Requested', icon: AlertCircle },
  BOTH_CONFIRMED: { className: 'bg-green-50 text-green-700 border-green-200', label: 'Both Confirmed', icon: Award },
  LOCKED: { className: 'bg-accent/10 text-accent border-accent/30', label: 'Locked', icon: Lock },
  SETTLED: { className: 'bg-green-100 text-green-800 border-green-300', label: 'Settled', icon: Shield },
};

const eventIcons = {
  LOAN_CREATED: FileText,
  BORROWER_INVITED: Share2,
  BORROWER_JOINED: User,
  TERMS_CONFIRMED: CheckCircle2,
  VIDEO_CONFIRMED: Video,
  LOAN_LOCKED: Lock,
  DOCUMENT_ADDED: Upload,
  PAYMENT_RECORDED: DollarSign,
  CLARIFICATION_REQUESTED: AlertCircle,
  RECONFIRMATION_SUBMITTED: Video,
  AGREEMENT_SETTLED: CheckCircle2,
  AGREEMENT_TEMPLATE_SELECTED: FileText,
  DISPUTE_RAISED: AlertTriangle,
  GENERAL_NOTE: FileText,
};

const eventLabels = {
  LOAN_CREATED: 'timeline.events.loan_created',
  BORROWER_INVITED: 'timeline.events.borrower_invited',
  BORROWER_JOINED: 'timeline.events.borrower_joined',
  TERMS_CONFIRMED: 'timeline.events.terms_confirmed',
  VIDEO_CONFIRMED: 'timeline.events.video_confirmed',
  LOAN_LOCKED: 'timeline.events.loan_locked',
  DOCUMENT_ADDED: 'timeline.events.document_added',
  PAYMENT_RECORDED: 'timeline.events.payment_recorded',
  CLARIFICATION_REQUESTED: 'timeline.events.clarification_requested',
  RECONFIRMATION_SUBMITTED: 'timeline.events.reconfirmation_submitted',
  AGREEMENT_SETTLED: 'timeline.events.agreement_settled',
  AGREEMENT_TEMPLATE_SELECTED: 'timeline.events.agreement_template_selected',
};

export default function AgreementDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { api, user, token } = useAuth();
  const { t, getTemplateLabel, getStatusLabel, formatCurrency: formatCurrencyWithLang } = useLanguage();
  const [agreement, setAgreement] = useState(null);
  const [events, setEvents] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showInviteShare, setShowInviteShare] = useState(false);
  const [inviteData, setInviteData] = useState(null);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [noteForm, setNoteForm] = useState({ note: '', is_dispute: false });
  const [paymentForm, setPaymentForm] = useState({ amount: '', payment_date: new Date().toISOString().split('T')[0], note: '' });
  const [submitting, setSubmitting] = useState(false);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const fetchAgreement = useCallback(async () => {
    try {
      const [agreementRes, eventsRes, docsRes, paymentsRes] = await Promise.all([
        api.get(`/agreements/${id}`),
        api.get(`/agreements/${id}/events`),
        api.get(`/agreements/${id}/documents`),
        api.get(`/agreements/${id}/payments`),
      ]);
      setAgreement(agreementRes.data);
      setEvents(eventsRes.data);
      setDocuments(docsRes.data);
      setPayments(paymentsRes.data);
    } catch {
      toast.error(t('errors.server_error'));
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  }, [api, id, navigate]);

  useEffect(() => { fetchAgreement(); }, [fetchAgreement]);

  const handleInvite = async () => {
    try {
      const res = await api.post(`/agreements/${id}/invite`);
      const link = `${window.location.origin}/invite/${res.data.token}`;
      setInviteData({
        link,
        messageTemplate: res.data.message_template,
        loanAmount: res.data.loan_amount,
      });
      setShowInviteShare(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create invite');
    }
  };

  const handleConfirm = async () => {
    try {
      const res = await api.post(`/agreements/${id}/confirm`);
      toast.success(res.data.message);
      fetchAgreement();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Confirmation failed');
    }
  };

  const handleSettle = async () => {
    try {
      const res = await api.post(`/agreements/${id}/settle`);
      toast.success(res.data.message);
      fetchAgreement();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Settlement failed');
    }
  };

  const handleNote = async (e) => {
    e.preventDefault();
    if (!noteForm.note.trim()) {
      toast.error('Please enter a note');
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/agreements/${id}/notes`, noteForm);
      toast.success(noteForm.is_dispute ? 'Dispute raised successfully' : 'Note added successfully');
      setShowNoteModal(false);
      setNoteForm({ note: '', is_dispute: false });
      fetchAgreement();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add note');
    } finally {
      setSubmitting(false);
    }
  };

  const handlePayment = async (e) => {
    e.preventDefault();
    if (!paymentForm.amount) return;
    setSubmitting(true);
    try {
      await api.post(`/agreements/${id}/payments`, {
        amount: parseFloat(paymentForm.amount),
        payment_date: paymentForm.payment_date,
        note: paymentForm.note,
      });
      toast.success(t('loan.agreement_detail.payment_recorded'));
      setShowPaymentModal(false);
      setPaymentForm({ amount: '', payment_date: new Date().toISOString().split('T')[0], note: '' });
      fetchAgreement();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('errors.failed_to_record_payment'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowed = ['application/pdf', 'image/jpeg', 'image/png'];
    if (!allowed.includes(file.type)) {
      toast.error(t('errors.file_type_not_allowed'));
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error(t('errors.file_too_large'));
      return;
    }
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await api.post(`/agreements/${id}/documents`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(t('agreement_detail.document_uploaded'));
      setShowUploadModal(false);
      fetchAgreement();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('errors.upload_failed'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleExport = () => {
    toast.info(t('common.info') || 'Generating PDF...');
    window.open(`${BACKEND_URL}/api/agreements/${id}/export?auth=${token}`, '_blank');
  };

  const handleDownloadDoc = async (doc) => {
    try {
      const res = await api.get(`/files/${doc.storage_path}`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.file_name;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Download failed');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-32" data-testid="agreement-detail-loading">
        <div className="animate-spin w-6 h-6 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!agreement) return null;

  const lender = agreement.participants?.find(p => p.role === 'LENDER');
  const borrower = agreement.participants?.find(p => p.role === 'BORROWER');
  const isLender = agreement.created_by_user_id === user?.id;
  const currency = agreement.currency || 'INR';
  const totalPaid = payments.reduce((sum, p) => sum + p.amount, 0);
  // Calculate interest if enabled
  const calculatedInterest = agreement.calculated_interest || 0;
  const totalAmount = agreement.total_amount || agreement.principal_amount;
  // Use current_total_due (pro-rata with minimum period) for outstanding
  const currentTotalDue = agreement.current_total_due || totalAmount;
  const outstanding = currentTotalDue - totalPaid;
  const status = statusConfig[agreement.status] || statusConfig.DRAFT;
  const hasBorrower = !!borrower;
  const requiresVideo = agreement.confirmation_mode !== 'TEXT';
  const canConfirm = !agreement.user_confirmed && agreement.status !== 'LOCKED' && agreement.status !== 'SETTLED' && hasBorrower && !requiresVideo;
  const videoConfirmations = agreement.video_confirmations || [];

  return (
    <div className="space-y-6" data-testid="agreement-detail-page">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate('/dashboard')}
          data-testid="back-to-dashboard"
          className="p-2 text-muted-foreground hover:text-foreground rounded-sm hover:bg-muted transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="font-serif text-2xl sm:text-3xl font-bold tracking-tight">Agreement Record</h1>
            <Badge className={`${status.className} text-xs uppercase tracking-wider font-semibold rounded-sm border`} data-testid="agreement-status-badge">
              {status.icon && <status.icon className="w-3 h-3 mr-1" />}
              {status.label}
            </Badge>
            {agreement.agreement_template && (
              <Badge className="bg-blue-50 text-blue-700 border-blue-200 text-xs uppercase tracking-wider font-semibold rounded-sm border" data-testid="agreement-template-badge">
                {agreement.agreement_template}
              </Badge>
            )}
          </div>
          <p className="text-xs text-muted-foreground font-mono mt-1">ID: {agreement.id.slice(0, 8)}</p>
        </div>
      </div>

      {/* Locked banner */}
      {agreement.status === 'LOCKED' && (
        <div className="bg-accent/5 border border-accent/20 p-4 flex items-center gap-3 animate-lock-glow" data-testid="locked-banner">
          <div className="w-10 h-10 bg-accent/10 rounded-sm flex items-center justify-center flex-shrink-0">
            <Shield className="w-5 h-5 text-accent" />
          </div>
          <div>
            <p className="font-semibold text-sm">Agreement Locked</p>
            <p className="text-xs text-muted-foreground">
              Confirmed by both parties on {agreement.locked_at?.slice(0, 10)}. Terms are now immutable.
            </p>
          </div>
        </div>
      )}

      {/* Settled banner */}
      {agreement.status === 'SETTLED' && (
        <div className="bg-green-50 border border-green-200 p-4 flex items-center gap-3" data-testid="settled-banner">
          <div className="w-10 h-10 bg-green-100 rounded-sm flex items-center justify-center flex-shrink-0">
            <CheckCircle2 className="w-5 h-5 text-green-700" />
          </div>
          <div>
            <p className="font-semibold text-sm text-green-900">Agreement Settled</p>
            <p className="text-xs text-green-700">
              This agreement has been successfully settled and closed.
            </p>
          </div>
        </div>
      )}

      {/* Dispute banner */}
      {agreement.has_open_dispute && (
        <div className="bg-amber-50 border border-amber-200 p-4 flex items-center justify-between gap-3" data-testid="dispute-banner">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-100 rounded-sm flex items-center justify-center flex-shrink-0">
              <AlertTriangle className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="font-semibold text-sm text-amber-800">Active Dispute / Clarification</p>
              <p className="text-xs text-muted-foreground">Please review the timeline note to resolve the conflict.</p>
            </div>
          </div>
          <Button 
            onClick={async () => {
              try {
                await api.post(`/agreements/${id}/resolve_dispute`);
                toast.success('Dispute marked as resolved');
                fetchAgreement();
              } catch (err) {
                toast.error('Failed to resolve dispute');
              }
            }}
            size="sm"
            className="bg-amber-600 hover:bg-amber-700 text-white h-9 px-4 rounded-sm font-semibold text-xs"
          >
            Mark Resolved
          </Button>
        </div>
      )}

      {/* Clarification banner */}
      {agreement.pending_clarification && (
        <div className="bg-orange-50 border border-orange-200 p-4 flex items-center gap-3" data-testid="clarification-banner">
          <div className="w-10 h-10 bg-orange-100 rounded-sm flex items-center justify-center flex-shrink-0">
            <AlertCircle className="w-5 h-5 text-orange-700" />
          </div>
          <div>
            <p className="font-semibold text-sm text-orange-900">Clarification Requested</p>
            <p className="text-xs text-orange-700">
              The principal party has requested clarification on the video confirmation.
            </p>
          </div>
        </div>
      )}

      {/* Summary card */}
      <div className="bg-white border border-border p-6 space-y-5">
        <h3 className="text-xs font-medium uppercase tracking-widest text-muted-foreground">Agreement Terms</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
          <div>
            <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Principal</p>
            <p className="font-serif text-2xl font-bold">{formatCurrency(agreement.principal_amount, currency)}</p>
          </div>

          {agreement.interest_enabled && (
            <>
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Interest Rate</p>
                <p className="font-serif text-2xl font-bold">{agreement.interest_rate}%</p>
                <p className="text-xs text-muted-foreground">{agreement.interest_frequency || 'MONTHLY'}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Interest (Full Duration)</p>
                <p className="font-serif text-2xl font-bold">{formatCurrency(calculatedInterest, currency)}</p>
              </div>
              {agreement.interest_details && (
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Current Interest Due</p>
                  <p className="font-serif text-2xl font-bold text-amber-600">{formatCurrency(agreement.current_interest_due, currency)}</p>
                  <p className="text-xs text-muted-foreground">
                    {agreement.interest_details.elapsed_months} mo elapsed
                    {agreement.minimum_interest_months > 0 && ` · Min ${agreement.minimum_interest_months} mo`}
                  </p>
                </div>
              )}
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Current Total Due</p>
                <p className="font-serif text-2xl font-bold text-primary">{formatCurrency(agreement.current_total_due || totalAmount, currency)}</p>
              </div>
            </>
          )}

          {!agreement.interest_enabled && (
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Interest</p>
              <p className="text-base font-semibold text-muted-foreground">Not Applicable</p>
            </div>
          )}

          {agreement.interest_enabled && agreement.duration_months > 0 && (
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">
                Installment ({agreement.interest_frequency || 'MONTHLY'})
              </p>
              <p className="font-serif text-2xl font-bold text-blue-600">
                {formatCurrency(totalAmount / (agreement.interest_frequency === 'QUARTERLY' ? Math.ceil(agreement.duration_months / 3) : agreement.interest_frequency === 'ANNUALLY' ? Math.ceil(agreement.duration_months / 12) : agreement.duration_months), currency)}
              </p>
            </div>
          )}
          
          <div>
            <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Duration</p>
            <p className="font-serif text-2xl font-bold">{agreement.duration_months}</p>
            <p className="text-xs text-muted-foreground">months</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Start Date</p>
            <p className="text-base font-semibold">{agreement.start_date}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Total Paid</p>
            <p className="text-base font-semibold text-green-700">{formatCurrency(totalPaid, currency)}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Outstanding</p>
            <p className="text-base font-semibold">{formatCurrency(Math.max(0, outstanding), currency)}</p>
          </div>
        </div>
        {agreement.notes && (
          <div>
            <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Notes</p>
            <p className="text-sm">{agreement.notes}</p>
          </div>
        )}
      </div>

      {/* Parties */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="bg-white border border-border p-5">
          <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">Principal Party</p>
          <p className="font-semibold">{lender?.user_name || 'Unknown'}</p>
          <p className="text-sm text-muted-foreground">{lender?.phone_number}</p>
          {agreement.confirmations?.find(c => c.user_id === lender?.user_id) && (
            <div className="flex items-center gap-1.5 mt-2 text-green-700">
              <CheckCircle2 className="w-3.5 h-3.5" />
              <span className="text-xs font-medium">
                {videoConfirmations.find(v => v.user_id === lender?.user_id) ? 'Video Confirmed' : 'Confirmed'}
              </span>
            </div>
          )}
        </div>
        <div className="bg-white border border-border p-5">
          <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">Secondary Party</p>
          {borrower ? (
            <>
              <p className="font-semibold">{borrower.user_name}</p>
              <p className="text-sm text-muted-foreground">{borrower.phone_number}</p>
              {agreement.confirmations?.find(c => c.user_id === borrower?.user_id) && (
                <div className="flex items-center gap-1.5 mt-2 text-green-700">
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  <span className="text-xs font-medium">
                    {videoConfirmations.find(v => v.user_id === borrower?.user_id) ? 'Video Confirmed' : 'Confirmed'}
                  </span>
                </div>
              )}
            </>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">{t('loan.invite.accept_title')}</p>
              {isLender && agreement.status !== 'LOCKED' && (
                <Button
                  onClick={handleInvite}
                  data-testid="invite-borrower-button"
                  className="bg-primary text-primary-foreground hover:bg-primary/90 h-9 px-4 rounded-sm uppercase tracking-wider font-semibold text-xs"
                >
                  <Share2 className="w-3.5 h-3.5 mr-2" />
                  {t('loan.detail.invite_link') || 'Generate Invite Link'}
                </Button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="flex flex-wrap gap-3">
        {canConfirm && (
          <Button
            onClick={handleConfirm}
            data-testid="confirm-agreement-button"
            className="bg-primary text-primary-foreground hover:bg-primary/90 h-11 px-6 rounded-sm uppercase tracking-wider font-semibold text-xs"
          >
            <CheckCircle2 className="w-4 h-4 mr-2" />
            Confirm Terms
          </Button>
        )}
        {agreement.status === 'LOCKED' && isLender && (
          <Button
            onClick={handleSettle}
            data-testid="settle-agreement-button"
            className="bg-green-600 text-white hover:bg-green-700 h-11 px-6 rounded-sm uppercase tracking-wider font-semibold text-xs m-0"
          >
            <CheckCircle2 className="w-4 h-4 mr-2" />
            {t('loan.detail.settle_agreement') || 'Settle Agreement'}
          </Button>
        )}
        {agreement.user_confirmed && agreement.status !== 'LOCKED' && agreement.status !== 'SETTLED' && agreement.status !== 'CLARIFICATION_REQUESTED' && (
          <div className="flex items-center gap-2 px-4 py-2 bg-green-50 border border-green-200 rounded-sm">
            <CheckCircle2 className="w-4 h-4 text-green-700" />
            <span className="text-sm font-medium text-green-700">{t('loan.confirmation.success')}</span>
          </div>
        )}
        {isLender && agreement.status === 'LOCKED' && (
          <>
            <Button
              onClick={() => setShowPaymentModal(true)}
              data-testid="add-payment-button"
              variant="outline"
              className="h-11 px-6 rounded-sm uppercase tracking-wider font-semibold text-xs border-primary text-primary"
            >
              <DollarSign className="w-4 h-4 mr-2" />
              {t('loan.detail.add_payment')}
            </Button>
            <Button
              onClick={() => setShowUploadModal(true)}
              data-testid="upload-document-button"
              variant="outline"
              className="h-11 px-6 rounded-sm uppercase tracking-wider font-semibold text-xs border-primary text-primary"
            >
              <Upload className="w-4 h-4 mr-2" />
              {t('loan.detail.upload_document')}
            </Button>
            <Button
              onClick={() => {
                const text = `Reminder: Payment of ${formatCurrency(outstanding, currency)} is due for our agreement. Please review details here: ${window.location.href}`;
                const phone = borrower?.phone_number || '';
                window.open(`https://wa.me/${phone.replace(/\D/g, '')}?text=${encodeURIComponent(text)}`, '_blank');
              }}
              data-testid="send-reminder-button"
              variant="outline"
              className="h-11 px-6 rounded-sm uppercase tracking-wider font-semibold text-xs border-green-600 text-green-700 hover:bg-green-50"
            >
              <MessageSquare className="w-4 h-4 mr-2" />
              Send Reminder
            </Button>
          </>
        )}
        <Button
          onClick={handleExport}
          data-testid="export-pdf-button"
          variant="outline"
          className="h-11 px-6 rounded-sm uppercase tracking-wider font-semibold text-xs border-primary text-primary"
        >
          <Download className="w-4 h-4 mr-2" />
          {t('loan.detail.export_pdf')}
        </Button>
        <Button
          onClick={() => setShowNoteModal(true)}
          data-testid="add-note-button"
          variant="outline"
          className="h-11 px-6 rounded-sm uppercase tracking-wider font-semibold text-xs border-amber-500 text-amber-600 hover:bg-amber-50"
        >
          <FileText className="w-4 h-4 mr-2" />
          Add Note / Dispute
        </Button>
      </div>

      {/* Main Action - Video Confirmation */}
      {requiresVideo && (!agreement.user_confirmed || agreement.status === 'CLARIFICATION_REQUESTED') && agreement.status !== 'LOCKED' && agreement.status !== 'SETTLED' && hasBorrower && (
        <div className="mt-6 mb-6">
          <VideoSequenceFlow
            agreement={agreement}
            onRefresh={fetchAgreement}
          />
        </div>
      )}

      {/* Tabs */}
      <Tabs defaultValue="timeline" className="w-full">
        <TabsList className="bg-muted p-1 rounded-sm w-full grid grid-cols-4 h-auto">
          <TabsTrigger value="timeline" data-testid="tab-timeline" className="rounded-sm text-xs uppercase tracking-wider font-semibold">
            <Clock className="w-3.5 h-3.5 mr-1.5" />
            {t('loan.detail.timeline')} ({events.length})
          </TabsTrigger>
          <TabsTrigger value="documents" data-testid="tab-documents" className="rounded-sm text-xs uppercase tracking-wider font-semibold">
            <FileText className="w-3.5 h-3.5 mr-1.5" />
            {t('loan.detail.documents')} ({documents.length})
          </TabsTrigger>
          <TabsTrigger value="payments" data-testid="tab-payments" className="rounded-sm text-xs uppercase tracking-wider font-semibold">
            <DollarSign className="w-3.5 h-3.5 mr-1.5" />
            {t('loan.detail.payments')} ({payments.length})
          </TabsTrigger>
          <TabsTrigger value="video" data-testid="tab-video" className="rounded-sm text-xs uppercase tracking-wider font-semibold">
            <Video className="w-3.5 h-3.5 mr-1.5" />
            {t('loan.detail.video_confirmations')}
          </TabsTrigger>
        </TabsList>

        {/* Timeline */}
        <TabsContent value="timeline" data-testid="timeline-content">
          <div className="space-y-0">
            {events.map((event, i) => {
              const Icon = eventIcons[event.event_type] || Clock;
              const isLock = event.event_type === 'LOAN_LOCKED';
              const isVideo = event.event_type === 'VIDEO_CONFIRMED';
              const isClarification = event.event_type === 'CLARIFICATION_REQUESTED';
              const isSettled = event.event_type === 'AGREEMENT_SETTLED';
              return (
                <div key={event.id} className="flex gap-4" data-testid={`event-${event.id}`}>
                  <div className="flex flex-col items-center">
                    <div className={`w-8 h-8 rounded-sm flex items-center justify-center flex-shrink-0 ${isLock ? 'bg-accent text-accent-foreground' :
                      isSettled ? 'bg-green-100 text-green-700' :
                        isVideo ? 'bg-primary text-primary-foreground' :
                          isClarification ? 'bg-orange-100 text-orange-700' :
                            'bg-muted text-muted-foreground'
                      }`}>
                      <Icon className="w-4 h-4" />
                    </div>
                    {i < events.length - 1 && <div className="w-px flex-1 bg-border min-h-[24px]" />}
                  </div>
                  <div className="pb-6 flex-1">
                    <p className={`text-sm font-semibold ${isLock ? 'text-accent' :
                      isSettled ? 'text-green-700' :
                        isVideo ? 'text-primary' :
                          isClarification ? 'text-orange-700' : ''
                      }`}>
                      {eventLabels[event.event_type] ? t(eventLabels[event.event_type], {
                        name: event.metadata?.user_name || event.created_by_name || '',
                        amount: event.metadata?.amount ? formatCurrency(event.metadata.amount, currency) : '',
                        filename: event.metadata?.file_name || '',
                        template: event.metadata?.template ? getTemplateName(event.metadata.template) : '',
                      }) : event.event_type === 'DISPUTE_RAISED' ? 'Dispute Raised' : event.event_type === 'GENERAL_NOTE' ? 'Note Added' : event.event_type}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {t('common.by') || 'by'} {event.metadata?.user_name || 'System'} &middot; {new Date(event.created_at).toLocaleString()}
                    </p>
                    {event.metadata && (
                      <div className="mt-2 text-xs text-muted-foreground space-y-1">
                        {event.metadata.note && (
                          <div className={`p-2 rounded-sm ${event.event_type === 'DISPUTE_RAISED' ? 'bg-amber-50 border border-amber-200 text-amber-900' : 'bg-muted text-muted-foreground'}`}>
                            "{event.metadata.note}"
                          </div>
                        )}
                        {event.metadata.amount && <p>{t('payment.add.amount_label')}: {formatCurrency(event.metadata.amount, currency)}</p>}
                        {event.metadata.file_name && <p>{t('loan.detail.documents')}: {event.metadata.file_name}</p>}
                        {event.metadata.user_name && !event.metadata.file_hash && <p>{event.metadata.user_name}</p>}
                        {event.metadata.file_hash && <p className="font-mono">{t('video.list.hash')}: {event.metadata.file_hash?.slice(0, 16)}...</p>}
                        {event.metadata.template && <p>{t('timeline.events.agreement_template_selected', { template: getTemplateName(event.metadata.template) })}</p>}
                        {event.metadata.identity_statement && <p className="italic">"{event.metadata.identity_statement}"</p>}
                        {event.metadata.reason && <p className="italic">{t('loan.clarification.clarification_reason')}: "{event.metadata.reason}"</p>}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
            {events.length === 0 && (
              <p className="text-center py-8 text-muted-foreground text-sm">{t('loan.detail.no_events') || 'No events yet'}</p>
            )}
          </div>
        </TabsContent>

        {/* Documents */}
        <TabsContent value="documents" data-testid="documents-content">
          <div className="space-y-3">
            {documents.map(doc => (
              <div key={doc.id} className="flex items-center justify-between bg-white border border-border p-4" data-testid={`document-${doc.id}`}>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-muted rounded-sm flex items-center justify-center">
                    <FileText className="w-5 h-5 text-muted-foreground" />
                  </div>
                  <div>
                    <p className="text-sm font-medium">{doc.file_name}</p>
                    <p className="text-xs text-muted-foreground">
                      {t('common.by')} {doc.uploaded_by_name} &middot; {new Date(doc.created_at).toLocaleDateString()}
                    </p>
                    <p className="text-xs text-muted-foreground font-mono">{t('common.hash')}: {doc.file_hash?.slice(0, 16)}...</p>
                  </div>
                </div>
                <button
                  onClick={() => handleDownloadDoc(doc)}
                  data-testid={`download-doc-${doc.id}`}
                  className="p-2 hover:bg-muted rounded-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  <Download className="w-4 h-4" />
                </button>
              </div>
            ))}
            {documents.length === 0 && (
              <p className="text-center py-8 text-muted-foreground text-sm">No documents uploaded</p>
            )}
          </div>
        </TabsContent>

        {/* Payments */}
        <TabsContent value="payments" data-testid="payments-content">
          <div className="space-y-3">
            {payments.length > 0 && (
              <div className="bg-primary/5 border border-primary/10 p-4 flex items-center justify-between">
                <span className="text-sm font-medium">Total Paid</span>
                <span className="font-serif text-xl font-bold text-primary">
                  {formatCurrency(totalPaid, currency)}
                </span>
              </div>
            )}
            {payments.map(pay => (
              <div key={pay.id} className="flex items-center justify-between bg-white border border-border p-4" data-testid={`payment-${pay.id}`}>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-50 rounded-sm flex items-center justify-center">
                    <DollarSign className="w-5 h-5 text-green-700" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold">{formatCurrency(pay.amount, currency)}</p>
                    <p className="text-xs text-muted-foreground">
                      {pay.payment_date} &middot; by {pay.created_by_name}
                    </p>
                    {pay.note && <p className="text-xs text-muted-foreground mt-0.5">{pay.note}</p>}
                  </div>
                </div>
              </div>
            ))}
            {payments.length === 0 && (
              <p className="text-center py-8 text-muted-foreground text-sm">No payments recorded</p>
            )}
          </div>
        </TabsContent>

        {/* Video Confirmations */}
        <TabsContent value="video" data-testid="video-content">
          <div className="mt-2">
            <VideoHistory
              agreement={agreement}
              onRefresh={fetchAgreement}
            />
          </div>
        </TabsContent>
      </Tabs>

      {/* Payment Modal */}
      <Dialog open={showNoteModal} onOpenChange={setShowNoteModal}>
        <DialogContent className="rounded-sm" data-testid="note-modal">
          <DialogHeader>
            <DialogTitle className="font-serif">Add Note / Dispute</DialogTitle>
            <DialogDescription>Add a timestamped note or raise a dispute on the agreement timeline for record keeping.</DialogDescription>
          </DialogHeader>

          <form onSubmit={handleNote} className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Message / Description</label>
              <textarea
                value={noteForm.note}
                onChange={e => setNoteForm(p => ({ ...p, note: e.target.value }))}
                placeholder="Describe detail note or issue..."
                rows={4}
                required
                className="w-full rounded-sm border border-input bg-white px-3 py-2 text-sm focus:border-primary focus:ring-1 focus:ring-primary outline-none resize-none"
              />
            </div>

            <div className="flex items-center space-x-2">
              <input 
                type="checkbox"
                id="is_dispute"
                checked={noteForm.is_dispute}
                onChange={e => setNoteForm(p => ({ ...p, is_dispute: e.target.checked }))}
                className="rounded border-gray-300 text-amber-600 focus:ring-amber-500"
              />
              <label htmlFor="is_dispute" className="text-sm font-medium text-amber-800">Raise as Dispute / Clarification</label>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowNoteModal(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting} className={noteForm.is_dispute ? "bg-amber-600 hover:bg-amber-700" : "bg-primary"}>
                {submitting ? 'Adding...' : (noteForm.is_dispute ? 'Raise Dispute' : 'Add Note')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={showPaymentModal} onOpenChange={setShowPaymentModal}>
        <DialogContent className="rounded-sm" data-testid="payment-modal">
          <DialogHeader>
            <DialogTitle className="font-serif">Record Payment</DialogTitle>
            <DialogDescription>Record a payment made towards this agreement. This is a record only.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handlePayment} className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Amount *</label>
              <Input
                data-testid="payment-amount-input"
                type="number"
                step="0.01"
                min="0"
                placeholder="500.00"
                value={paymentForm.amount}
                onChange={e => setPaymentForm(prev => ({ ...prev, amount: e.target.value }))}
                className="h-11 rounded-sm"
                required
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Payment Date *</label>
              <Input
                data-testid="payment-date-input"
                type="date"
                value={paymentForm.payment_date}
                onChange={e => setPaymentForm(prev => ({ ...prev, payment_date: e.target.value }))}
                className="h-11 rounded-sm"
                required
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Note</label>
              <Input
                data-testid="payment-note-input"
                placeholder="e.g. Monthly installment"
                value={paymentForm.note}
                onChange={e => setPaymentForm(prev => ({ ...prev, note: e.target.value }))}
                className="h-11 rounded-sm"
              />
            </div>
            <DialogFooter>
              <Button
                type="submit"
                data-testid="submit-payment-button"
                disabled={submitting}
                className="bg-primary text-primary-foreground hover:bg-primary/90 h-11 px-6 rounded-sm uppercase tracking-wider font-semibold text-xs"
              >
                {submitting ? 'Recording...' : 'Record Payment'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Upload Modal */}
      <Dialog open={showUploadModal} onOpenChange={setShowUploadModal}>
        <DialogContent className="rounded-sm" data-testid="upload-modal">
          <DialogHeader>
            <DialogTitle className="font-serif">Upload Document</DialogTitle>
            <DialogDescription>Upload supporting documents. Accepted: PDF, JPG, PNG (max 10MB). A SHA256 hash will be generated for integrity.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="border-2 border-dashed border-border rounded-sm p-8 text-center">
              <Upload className="w-8 h-8 text-muted-foreground mx-auto mb-3" />
              <p className="text-sm text-muted-foreground mb-3">Click to select or drag file here</p>
              <input
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={handleUpload}
                data-testid="file-upload-input"
                className="block w-full text-sm text-muted-foreground file:mr-4 file:py-2 file:px-4 file:rounded-sm file:border-0 file:text-xs file:font-semibold file:bg-primary file:text-primary-foreground hover:file:bg-primary/90 file:uppercase file:tracking-wider cursor-pointer"
              />
            </div>
            {submitting && (
              <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                <div className="animate-spin w-4 h-4 border-2 border-primary border-t-transparent rounded-full" />
                Uploading...
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Invite Share Dialog */}
      {inviteData && (
        <InviteShareDialog
          open={showInviteShare}
          onOpenChange={setShowInviteShare}
          inviteLink={inviteData.link}
          messageTemplate={inviteData.messageTemplate}
          loanAmount={inviteData.loanAmount}
          currency={currency}
        />
      )}
    </div>
  );
}
